import { ReactElement } from 'react'

import { Dropdown, MenuProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Icon } from 'components/core'
import { UserBadge } from 'components/UserBadge/UserBadge'
import { signOut } from 'features/Auth/authService'
import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { useCurrentOrganization, useUserHasPermissions } from 'hooks'
import { getPath } from 'routing/pagePaths'
import { RootReduxState } from 'types'

import { NavButton } from './NavButton'
import * as styles from './styles.scss'

export type ProfileNavItemProps = {
    disableSettingsNavigation?: boolean
}

export const ProfileNavItem = ({
    disableSettingsNavigation = false,
}: ProfileNavItemProps): ReactElement => {
    const { t } = useTranslation('common')
    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )
    const userEmail =
        useSelector((state: RootReduxState) => state.auth.email) ?? ''
    const currentOrganization = useCurrentOrganization()
    const navigate = useNavigate()

    const settingsDisabledTitle = disableSettingsNavigation
        ? t(
              'common:settingsNavigationDisabledWhenOrgLocked',
              'Settings are unavailable while your organization is locked.'
          )
        : undefined

    const goToSettings = (): void => {
        navigate(getPath('settingsPage'))
    }

    const header: MenuProps['items'] = [
        {
            key: 'profileHeader',
            label: (
                <div className={styles['profile-info']}>
                    <div className={styles['organization-text']}>
                        {currentOrganization?.name ?? ''}
                    </div>
                    <div className={styles['email-text']}>{userEmail}</div>
                </div>
            ),
            icon: <UserBadge email={userEmail} variant="avatar" />,
            disabled: disableSettingsNavigation,
            title: settingsDisabledTitle,
            onClick: disableSettingsNavigation ? undefined : goToSettings,
        },
    ]

    const settings: MenuProps['items'] = [
        {
            key: 'settingsPage',
            label: t('common:settings', 'Settings'),
            icon: <Icon name="SETTINGS" size={16} />,
            disabled: disableSettingsNavigation,
            title: settingsDisabledTitle,
            onClick: disableSettingsNavigation ? undefined : goToSettings,
        },
    ]

    const logOut: MenuProps['items'] = [
        {
            key: 'logOut',
            label: t('common:logOut', 'Log Out'),
            icon: <Icon name="UPLOAD" size={15} rotate={90} />,
            onClick: () => {
                signOut()
                navigate(getPath('authPage'))
            },
        },
    ]

    const items: MenuProps['items'] = [
        ...header,
        { type: 'divider' },
        ...(dashboardOnlyExperience ? [] : settings),
        ...logOut,
    ]

    return (
        <Dropdown
            overlayClassName={styles['profile-menu']}
            trigger={['click']}
            placement="bottomLeft"
            menu={{ items }}
        >
            <NavButton
                tooltipText={t('common:account', 'Account')}
                id="profile-nav-button"
            >
                <Icon name="USER_OUTLINE" size={18} />
            </NavButton>
        </Dropdown>
    )
}
